import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	AlertCircle,
	BookOpenIcon,
	Loader2,
	RefreshCw,
	Users,
	UsersIcon,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbList,
	BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Container from "@/components/ui/container";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import cacheKeys from "@/config/cache-keys";
import pages from "@/config/pages";
import {
	dedupeMainAccountIds,
	emitSyncDisabledDriftResultToasts,
	fetchDriftCountByTypeMap,
	syncDisabledDriftFromTrees,
} from "@/lib/syncDisabledProductDrift";
import AppService from "@/services/AppService";
import type { InsuranceProductDefinitionRaw } from "@/types/insurance/productDefinition";
import { productDefinitionsToTemplates } from "@/types/insurance/productDefinition";
import { CatalogDriftSummary } from "./CatalogDriftSummary";
import { ParameterDisplay } from "./ParameterDisplay";
import { ProductDriftModal } from "./ProductDriftModal";

const appService = new AppService();

const ProductDefinitionsPage = () => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	const [driftTarget, setDriftTarget] = useState<{
		type: string;
		name: string;
	} | null>(null);

	// Populated after the first drift check — counts drift instances per product type.
	const [driftCountByType, setDriftCountByType] = useState<Map<string, number>>(
		new Map(),
	);

	const [catalogDriftOpen, setCatalogDriftOpen] = useState(false);
	const [syncingProductType, setSyncingProductType] = useState<string | null>(
		null,
	);
	const [unsafeCatalogSyncAcknowledged, setUnsafeCatalogSyncAcknowledged] =
		useState(false);

	const { data, isLoading, isError } = useQuery({
		queryKey: cacheKeys.INSURANCE_PRODUCT_DEFINITIONS,
		queryFn: async () => {
			const response = await appService.getInsuranceProductDefinitions();
			return response.Result ?? [];
		},
		staleTime: 1000 * 60 * 60,
		gcTime: 1000 * 60 * 60 * 24,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});

	const products: InsuranceProductDefinitionRaw[] = data ?? [];

	const catalogTemplates = useMemo(
		() => productDefinitionsToTemplates(products),
		[products],
	);

	const syncDisabledDriftFromCatalogCard = useCallback(
		async (productType: string) => {
			const template = catalogTemplates.find((def) => def.type === productType);
			if (!template) {
				toast.error(
					t("admin.product-definitions.drift-modal.sync-no-template"),
				);
				return;
			}

			setSyncingProductType(productType);
			try {
				const accRes = await appService.getAllAccounts();
				const ids = dedupeMainAccountIds(accRes.Result ?? []);
				const trees = await Promise.all(
					ids.map(async (id) => {
						try {
							const r = await appService.getMainAccountTree(id);
							return r.Result;
						} catch {
							return undefined;
						}
					}),
				);

				const { updatedAccounts, failedAccounts } =
					await syncDisabledDriftFromTrees({
						productType,
						templates: catalogTemplates,
						trees,
						appService,
						queryClient,
						onlyRowsWithoutGroups: !unsafeCatalogSyncAcknowledged,
					});

				emitSyncDisabledDriftResultToasts(t, updatedAccounts, failedAccounts);

				const nextCounts = await fetchDriftCountByTypeMap(
					appService,
					catalogTemplates,
				);
				setDriftCountByType(nextCounts);
			} finally {
				setSyncingProductType(null);
			}
		},
		[catalogTemplates, queryClient, t, unsafeCatalogSyncAcknowledged],
	);

	if (isLoading) {
		return (
			<Container className="flex items-center justify-center py-20 gap-2">
				<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
				<span className="text-muted-foreground">
					{t("admin.product-definitions.loading")}
				</span>
			</Container>
		);
	}

	if (isError) {
		return (
			<Container className="py-10">
				<Card className="border-destructive bg-destructive/10">
					<CardHeader className="flex flex-row items-center gap-2">
						<AlertCircle className="h-5 w-5 text-destructive" />
						<CardTitle className="text-destructive">
							{t("admin.product-definitions.error-title")}
						</CardTitle>
					</CardHeader>
					<CardContent>
						<p className="text-sm text-muted-foreground">
							{t("admin.product-definitions.error-description")}
						</p>
					</CardContent>
				</Card>
			</Container>
		);
	}

	return (
		<div className="bg-soft-white dark:bg-[#121212] min-h-screen">
			<Container className="py-6 space-y-6">
				<Breadcrumb>
					<BreadcrumbList>
						<BreadcrumbItem>
							<Link
								to={pages.Admin}
								className="text-sm text-muted-foreground hover:text-foreground transition-colors"
							>
								{t("admin.product-definitions.breadcrumb-admin")}
							</Link>
						</BreadcrumbItem>
						<BreadcrumbSeparator />
						<BreadcrumbItem>
							<span className="text-sm font-medium">
								{t("admin.product-definitions.breadcrumb-current")}
							</span>
						</BreadcrumbItem>
					</BreadcrumbList>
				</Breadcrumb>

				<div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
					<Button
						type="button"
						variant="outline"
						size="sm"
						className="gap-2 shrink-0 self-start sm:self-auto"
						onClick={() => setCatalogDriftOpen(true)}
					>
						<Users className="h-4 w-4" />
						{t("admin.product-definitions.catalog-scan.button")}
					</Button>
					{unsafeCatalogSyncAcknowledged ? (
						<div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
							<Badge variant="destructive" className="text-xs font-normal">
								{t("admin.product-definitions.sync-catalog-ack-badge")}
							</Badge>
							<Button
								type="button"
								variant="ghost"
								size="sm"
								className="h-8 text-xs"
								onClick={() => setUnsafeCatalogSyncAcknowledged(false)}
							>
								{t("admin.product-definitions.sync-catalog-revoke")}
							</Button>
						</div>
					) : (
						<Button
							type="button"
							variant="outline"
							size="sm"
							className="gap-2 border-amber-200/80 bg-amber-50/50 text-amber-950 hover:bg-amber-100/80 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100 dark:hover:bg-amber-950/50 shrink-0 self-start sm:self-auto"
							onClick={() => setUnsafeCatalogSyncAcknowledged(true)}
						>
							{t("admin.product-definitions.sync-catalog-ack-button")}
						</Button>
					)}
				</div>

				<CatalogDriftSummary
					definitions={products}
					active={catalogDriftOpen}
					onClose={() => setCatalogDriftOpen(false)}
					onDriftResolved={setDriftCountByType}
				/>

				<div className="flex items-center justify-between">
					<div className="flex items-center gap-3">
						<BookOpenIcon className="h-6 w-6 text-muted-foreground" />
						<div>
							<h1 className="text-2xl font-semibold tracking-tight">
								{t("admin.product-definitions.title")}
							</h1>
							<p className="text-sm text-muted-foreground mt-0.5">
								{t("admin.product-definitions.subtitle")}
							</p>
						</div>
					</div>
					<Badge variant="secondary" className="text-sm rounded">
						{products.length} {t("admin.product-definitions.count-label")}
					</Badge>
				</div>

				<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
					{products.map((product) => {
						const productName = t(
							`admin.insurances.type-to-name.${product.insurance_type}`,
							{ defaultValue: product.label },
						);

						const driftCount = driftCountByType.get(product.insurance_type);

						return (
							<Card key={product.id} className="flex flex-col">
								<CardHeader className="pb-3">
									<div className="flex items-start justify-between gap-2">
										<CardTitle className="text-base font-semibold leading-snug">
											{productName}
										</CardTitle>
										<div className="flex items-center gap-1.5 shrink-0">
											{driftCount !== undefined && driftCount > 0 && (
												<Badge
													variant="destructive"
													className="text-xs tabular-nums"
												>
													{driftCount}
												</Badge>
											)}
											<Badge variant="outline" className="text-xs font-mono">
												{product.insurance_type}
											</Badge>
											<Tooltip>
												<TooltipTrigger asChild>
													<Button
														variant="ghost"
														size="icon"
														className="h-7 w-7"
														onClick={() =>
															setDriftTarget({
																type: product.insurance_type,
																name: productName,
															})
														}
													>
														<UsersIcon className="h-4 w-4 text-muted-foreground" />
													</Button>
												</TooltipTrigger>
												<TooltipContent>
													{t("admin.product-definitions.drift-button-tooltip")}
												</TooltipContent>
											</Tooltip>
											{driftCount !== undefined && driftCount > 0 ? (
												<Tooltip>
													<TooltipTrigger asChild>
														<Button
															type="button"
															variant="outline"
															size="sm"
															className="h-7 gap-1 px-2 text-xs shrink-0"
															disabled={syncingProductType !== null}
															onClick={() =>
																void syncDisabledDriftFromCatalogCard(
																	product.insurance_type,
																)
															}
														>
															{syncingProductType === product.insurance_type ? (
																<Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
															) : (
																<RefreshCw className="h-3.5 w-3.5 shrink-0" />
															)}
															<span className="hidden sm:inline">
																{unsafeCatalogSyncAcknowledged
																	? t("admin.product-definitions.sync-all-card")
																	: t(
																			"admin.product-definitions.sync-disabled-card",
																		)}
															</span>
														</Button>
													</TooltipTrigger>
													<TooltipContent>
														{unsafeCatalogSyncAcknowledged
															? t(
																	"admin.product-definitions.sync-all-card-tooltip",
																)
															: t(
																	"admin.product-definitions.sync-disabled-card-tooltip",
																)}
													</TooltipContent>
												</Tooltip>
											) : null}
										</div>
									</div>
								</CardHeader>

								<CardContent className="flex-1">
									{product.default_parameters.length === 0 ? (
										<p className="text-xs text-muted-foreground italic">
											{t("admin.product-definitions.no-params")}
										</p>
									) : (
										<div className="space-y-2">
											<p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
												{t("admin.product-definitions.params-heading")} (
												{product.default_parameters.length})
											</p>
											<div className="flex flex-wrap gap-2">
												{product.default_parameters.map((param) => (
													<ParameterDisplay
														key={param.type}
														parameter={param}
													/>
												))}
											</div>
										</div>
									)}
								</CardContent>
							</Card>
						);
					})}
				</div>
			</Container>

			<ProductDriftModal
				open={!!driftTarget}
				onClose={() => setDriftTarget(null)}
				productType={driftTarget?.type ?? ""}
				productName={driftTarget?.name ?? ""}
				onDriftResolved={setDriftCountByType}
			/>
		</div>
	);
};

export default ProductDefinitionsPage;
